"use client";

import {
  AuctionState,
  AuctionType,
  BidType,
  BiddingSchedule,
  BiddingScheduleRange,
  HighestBid,
  Lot,
  Role,
} from "@prisma/client";
import { Flex } from "@radix-ui/themes";
import { useParams } from "next/navigation";
import React, { useCallback, useEffect, useReducer, useRef, useState } from "react";
import CurrentBid from "./CurrentBid";
import { calculateBidIncrement as calculateIncrement, cn, fetcher } from "@/lib/utils";
import axios, { AxiosError, HttpStatusCode } from "axios";
import useCustomToast from "@/lib/hooks/useCustomToast";
import OtherBids from "./OtherBids";
import BiddingCardWinnerMessage from "./CardWidgets/BiddingCardWinnerMessage";
import BiddingCardInput from "./CardWidgets/BiddingCardInput";
import BID_ACTIONS from "./BidActions";
import LoginPopup from "@/app/components/LoginPopup";
import LotLogInBanner from "@/app/components/LotLogInBanner";
import BidPopup from "@/app/components/BidPopup";
import { Separator } from "@/app/components/ui/separator";
import LotBidHistory, {
  LOT_BID_HISTORY_MAX_BID_QUERY_KEY,
  LOT_BID_HISTORY_QUERY_KEY,
} from "./history/LotBidHistory";
import useSWR, { useSWRConfig } from "swr"
import { SavePaymentSelectionHandle } from "@/app/components/creditCardSelect/PaymentMethodSelect";
import { respectsBiddingSchedule } from "@/lib/bidding/util";
import { useSharedSubscription } from "@/app/hooks/useSharedNATS";
import BidInstructions from "./BidInstructions";
import { FLASH_DURATION } from "@/lib/constants";
import { useLotUser } from "./LotUserContext";

export interface BidInfo {
  amount: number;
  bidType: BidType;
  flash?: boolean;
}

interface Props {
  biddingSchedule: BiddingSchedule & {
    BiddingScheduleRange: BiddingScheduleRange[];
  };
  lot: Lot;
  expired: boolean;
  currentBid: HighestBid | null;
  auctionStart: Date;
  auctionEnd: Date;
  auctionState: AuctionState;
  auctionType: AuctionType;
}

const NOTIFICATION_DURATION = 3 * 1000;
const DB_POLL_DURATION = 5 * 1000;
const NO_CURRENT_HIGHEST_BID = 0;

const VALID_BID_STATES: AuctionState[] = [
  AuctionState.AUCTION,
  AuctionState.SOFT_CLOSING,
];

const INVALID_BID_STATES: AuctionState[] = [
  AuctionState.PREVIEW,
  AuctionState.CREATING,
  AuctionState.PAUSED,
  AuctionState.POST_AUCTION,
  AuctionState.MANUALLY_ENDED,
];

interface BidState {
  initialized: boolean;
  currentHighestBid: number;
  currentWinner: string | undefined;
  isCurrentUserWinner: boolean;
  flash: boolean;
}

const REFRESH_INTERVAL = 4_000;

const BiddingCard = ({
  biddingSchedule,
  lot,
  expired,
  currentBid: initialBid,
  auctionStart,
  auctionEnd,
  auctionState,
  auctionType,
}: Props) => {
  const { auctionId } = useParams();
  const { userId: lotUserId, hasUserBids: hasUserBidsInitial, role: lotUserRole } = useLotUser();
  const lotId = lot.id.toString();

  const { mutate } = useSWRConfig();

  const calculateBidMin = useCallback((currentBid: number | undefined) => {
    if (currentBid) {
      return currentBid + calculateIncrement({
        currentBid,
        ranges: biddingSchedule.BiddingScheduleRange,
      });
    }
    return lot.startBid!;
  }, [biddingSchedule.BiddingScheduleRange, lot.startBid]);

  const calculateBidIncrement = useCallback((currentBid: number | undefined) => {
    return calculateIncrement({
      currentBid: currentBid || lot.startBid!,
      ranges: biddingSchedule.BiddingScheduleRange,
    });
  }, [biddingSchedule.BiddingScheduleRange, lot.startBid]);

  const {
  data: currentHighestBid,
  mutate: mutateCurrentHighestBid,
  } = useSWR<HighestBid | null>(
    `/api/auctions/${auctionId}/lots/${lotId}/highestBid`,
    fetcher,
    {
      fallbackData: initialBid,
      revalidateOnFocus: true,
      revalidateOnMount: true,
      refreshInterval: REFRESH_INTERVAL,
      onSuccess: (data) => {
        if (!data || typeof (data as any).amount !== "number") return;
        if ((data as any).amount > (stateRef.current?.currentHighestBid ?? NO_CURRENT_HIGHEST_BID)) {
          onNewHighestBid(data);
        }
      },
    }
  );

  const [bidMin, setBidMin] = useState(() => calculateBidMin(currentHighestBid?.amount));
  const [bidIncrement, setBidIncrement] = useState(() => calculateBidIncrement(currentHighestBid?.amount));

  useEffect(() => {
    setBidMin(calculateBidMin(currentHighestBid?.amount));
    setBidIncrement(calculateBidIncrement(currentHighestBid?.amount));
  }, [currentHighestBid, calculateBidMin, calculateBidIncrement]);

  const initialState: BidState = {
    initialized: false,
    currentHighestBid: initialBid?.amount ?? NO_CURRENT_HIGHEST_BID,
    currentWinner: initialBid?.userId,
    isCurrentUserWinner: false,
    flash: false,
  };

  const sessionRef = useRef<string | null | undefined>(lotUserId);
  const [displayLoginPopup, setDisplayLoginPopup] = useState(false);
  const [displayBidPopup, setDisplayBidPopup] = useState(false);
  const [state, dispatch] = useReducer(reducer, initialState);
  const { toast, toastError } = useCustomToast();
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [disableBid, setDisableBid] = useState(
    Object.values(INVALID_BID_STATES).includes(auctionState)
  );
  const [bidInfo, setBidInfo] = useState<BidInfo>({
    amount: 0,
    bidType: BidType.STANDARD,
  });

  // Track whether the current user has ever placed a bid on this lot.
  // Seed from server-computed value and treat as sticky true thereafter.
  const [hasUserBids, setHasUserBids] = useState(hasUserBidsInitial);

  // // Manage flashing in BidPopup when the minimum required bid increases while the popup is open
  // const popupSyncInitialRender = useRef(true);
  // const flashTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // // When popup opens, reset the initial render guard
  // useEffect(() => {
  //   if (displayBidPopup) {
  //     popupSyncInitialRender.current = true;
  //     // Ensure we don't carry over a lingering flash from a previous session
  //     setBidInfo(prev => (prev.flash ? { ...prev, flash: false } : prev));
  //   } else {
  //     // Cleanup any pending flash timer when popup closes
  //     if (flashTimeoutRef.current) {
  //       clearTimeout(flashTimeoutRef.current);
  //       flashTimeoutRef.current = null;
  //     }
  //     // Also clear any lingering flash when closing the popup
  //     setBidInfo(prev => (prev.flash ? { ...prev, flash: false } : prev));
  //   }
  // }, [displayBidPopup]);

  // useEffect(() => {
  //   if (!displayBidPopup) return;
  //   // While submitting/confirming a bid, freeze BidPopup to avoid confusing updates
  //   if (isSubmitting) return;

  //   // If the current user is the highest bidder, don't change amount or flash
  //   const isCurrentUserHighest = currentHighestBid?.userId && sessionRef.current && currentHighestBid.userId === sessionRef.current;

  //   if (popupSyncInitialRender.current) {
  //     popupSyncInitialRender.current = false;
  //     if (!isCurrentUserHighest) {
  //       // On initial open, ensure amount is at least bidMin without flashing
  //       setBidInfo(prev => {
  //         if (prev.amount < bidMin) {
  //           return { ...prev, amount: bidMin, flash: false };
  //         }
  //         return prev;
  //       });
  //     }
  //     return;
  //   }

  //   if (isCurrentUserHighest) return;

  //   // On subsequent increases, raise amount only if required and flash briefly
  //   if (flashTimeoutRef.current) {
  //     clearTimeout(flashTimeoutRef.current);
  //     flashTimeoutRef.current = null;
  //   }

  //   setBidInfo(prev => {
  //     if (bidMin > prev.amount) {
  //       const updated = { ...prev, amount: bidMin, flash: true } as BidInfo;
  //       flashTimeoutRef.current = setTimeout(() => {
  //         setBidInfo(p => ({ ...p, flash: false }));
  //       }, FLASH_DURATION);
  //       return updated;
  //     }
  //     return prev;
  //   });
  // }, [bidMin, displayBidPopup, currentHighestBid, sessionRef, isSubmitting]);

  const stateRef = useRef<BidState>(initialState);
  const savePaymentSelectionRef = useRef<SavePaymentSelectionHandle>(null);

  function reducer(state: BidState, action: { type: string; payload?: any }) {
    switch (action.type) {
      case BID_ACTIONS.INITIALIZE:
        if (!action.payload.userId || state.initialized) return state;
        return {
          ...state,
          initialized: true,
          isCurrentUserWinner:
            initialBid?.userId === action.payload.userId,
        };

      case BID_ACTIONS.UPDATE_HIGHEST_BID:
        const { amount, userId } = action.payload;
        if (!amount || !userId) return state;

        return {
          ...state,
          currentHighestBid: amount,
          currentWinner: userId,
          isCurrentUserWinner: userId === (sessionRef.current ?? lotUserId),
          flash: true,
        };

      case BID_ACTIONS.UNFLASH:
        return { ...state, flash: false };
    }
    throw Error("Unknown action.");
  }

  useEffect(() => {
    setDisableBid(Object.values(INVALID_BID_STATES).includes(auctionState));
  }, [auctionState, auctionStart, auctionEnd]);

  useEffect(() => {
    // Initialize the bidding state with the user id hydrated from the server
    // via LotUserContext so we have a stable id on first render.
    const effectiveUserId = lotUserId ?? null;
    dispatch({ type: BID_ACTIONS.INITIALIZE, payload: { userId: effectiveUserId } });
    sessionRef.current = effectiveUserId;
    // console.log("sessionRef.current", sessionRef.current);
  }, [lotUserId]);

  // Solves stale closure problem for onNewHighestBid.
  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  const onNewHighestBid = useCallback((payload: any) => {
    // Ignore bids that are less than or equal to the current highest bid.
    if (payload.amount <= stateRef.current?.currentHighestBid) return;

    mutateCurrentHighestBid(payload as HighestBid);
    mutate([LOT_BID_HISTORY_QUERY_KEY, auctionId, lotId]);
    mutate([LOT_BID_HISTORY_MAX_BID_QUERY_KEY, auctionId, lotId]);
    if (
      stateRef.current?.isCurrentUserWinner &&
      payload?.userId !== stateRef.current?.currentWinner
    ) {
      toast({
        title: "You've been outbid.",
        description: `Someone has outbid you.`,
        duration: NOTIFICATION_DURATION,
      });
    } else if (
      payload?.userId === sessionRef.current && 
      payload?.isAuto === true
    ) {
      toast({
        title: "Your max bid triggered a bid.",
        description: `The system bid $${payload.amount} on your behalf.`,
        duration: NOTIFICATION_DURATION,
      });
    }

    if (!hasUserBids && payload?.userId === sessionRef.current) {
      setHasUserBids(true);
    }

    dispatch({
      type: BID_ACTIONS.UPDATE_HIGHEST_BID,
      payload: {
        amount: payload.amount,
        userId: payload.userId,
      },
    });

    const newBidIncrement = calculateIncrement({
      currentBid: payload.amount,
      ranges: biddingSchedule.BiddingScheduleRange,
    });

    setBidIncrement(newBidIncrement);
    setBidMin(payload.amount + newBidIncrement);
  }, [biddingSchedule.BiddingScheduleRange, mutateCurrentHighestBid, toast, auctionId, lotId, mutate, hasUserBids]);

  useSharedSubscription({
    topic: `postgres.${process.env.NEXT_PUBLIC_DB_SCHEMA!}.Lot${lotId}.HighestBid`,
    onMessage: onNewHighestBid,
  });

  const placeBid = async ({ amount, bidType }: BidInfo) => {
    if (lotUserRole === Role.ADMIN) {
      alert("Admins are not allowed to bid.")
      return;
    }

    if (disableBid) {
      toastError({
        title: "Auction unavailable for bidding",
        description:
          "You are currently unable to place a bid because the auction is not active.",
      });
      return;
    }
    // Fetch the current time and compare it directly to lot end Time

    if (expired) {
      toastError({
        title: "The lot has closed.",
        description:
          "You are currently unable to place a bid because this lot has closed.",
      });
      return;
    }

    // If there is no hydrated user id, prompt the user to log in before bidding.
    if (!lotUserId) {
      setDisplayLoginPopup(true);
      return;
    }

    setIsSubmitting(true);
    if (isNaN(amount)) {
      setError("Please enter a valid bid.");
      setIsSubmitting(false);
      return;
    } else if (amount < bidMin) {
      setError(`Please bid at least $${bidMin} to proceed.`);
      setIsSubmitting(false);
      return;
    } else if (Math.floor(amount) !== amount) {
      setError(`Please enter a full dollar amount to proceed.`);
      setIsSubmitting(false);
      return;
    } else if (bidType === BidType.STANDARD && !respectsBiddingSchedule(amount, biddingSchedule)) {
      setError(`Please enter a bid that respects the bid increment schedule.`);
      setIsSubmitting(false);
      return
    } else {
      setError("");
    }

    // Bid is valid. Confirm it.
    if (lotUserRole === Role.BUYER) {
      setDisplayBidPopup(true);
      setBidInfo({ amount: amount, bidType: bidType });
    }

    setIsSubmitting(false);
  };

  const confirmBid = async ({ amount, bidType }: BidInfo) => {
    // Optimistically update the UI

    const previousHighestBid = JSON.parse(JSON.stringify(currentHighestBid));
    if (bidType === BidType.STANDARD && !!sessionRef.current) {
      await mutateCurrentHighestBid(
        ({ ...(currentHighestBid as any), amount, userId: sessionRef.current } as unknown) as HighestBid,
        false
      );
    }

    try {
      const response = await axios.post(
        `/api/auctions/${auctionId}/lots/${lotId}/bids`,
        {
          session: { userId: sessionRef.current ?? lotUserId },
          amount,
          bidType,
        }
      );

      savePaymentSelectionRef.current?.savePaymentSelection();

      // Don't optimistically update these, because we can't revert them on error.
      mutate([LOT_BID_HISTORY_QUERY_KEY, auctionId, lotId]);

      if (bidType === BidType.MAX) {
        mutate([LOT_BID_HISTORY_MAX_BID_QUERY_KEY, auctionId, lotId]);
      }

      if (response.data?.userId !== sessionRef.current && response.data?.amount === amount) {
        toast({
          title: "Max bid beat your bid!",
          description: "Your bid was placed, but someone set a max bid at the same amount or higher before you. To take the lead, please place a higher bid.",
          duration: 10000
        });
      } else if (response.data?.userId === sessionRef.current) {
        toast({
          title: "Bid successful!",
          description: `Your ${bidType === BidType.MAX ? "max " : ""}bid of $${amount} was successful.`,
        });
      }

      // Subscribe to the lot
      await fetch(`/api/account/subscriptions/subscribe`, {
        method: "POST",
        body: JSON.stringify({
          subscribe: true,
          lotId: parseInt(lotId as string),
          userId: sessionRef.current,
          auctionId: parseInt(auctionId as string)
        })
      })

      mutate(`/api/account/subscriptions/${sessionRef.current}/${lotId}`, {
        isSubscribed: true
      })
      mutate(`/api/auctions/${auctionId}/lots/${lotId}/followers`);
    } catch (error) {
      // console.log('error', error)

      // Revert optimistic updates on error
      mutate([LOT_BID_HISTORY_QUERY_KEY, auctionId, lotId]);
      if (bidType === BidType.MAX) {
        mutate([LOT_BID_HISTORY_MAX_BID_QUERY_KEY, auctionId, lotId]);
      }

      if (error instanceof AxiosError) {
        console.error(JSON.stringify(error.response, null, 2));
        if (error?.response?.data?.error === "flagged") {
          toastError({
            title: "Bid Failed",
            description:
              "Your account has been suspended for suspicious activity. As a result, your bid has been rejected.",
          });
        } else {
          toastError({
            title: "Bidding Error",
            description: error?.response?.data?.message,
          });
        }

        if (error.status !== HttpStatusCode.Conflict) {
          mutateCurrentHighestBid(previousHighestBid, false);
        } else {
          mutateCurrentHighestBid();
        }
      } else {
        throw error;
      }
    }
  };

  const focusPlaceholderRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    !displayBidPopup && focusPlaceholderRef.current?.focus();
  }, [displayBidPopup]);

  const isCurrentUserHighestBidder = useCallback(() => {
    if (!currentHighestBid) return false;
    const currentUserId = sessionRef.current ?? lotUserId;
    return !!currentUserId && currentHighestBid.userId === currentUserId;
  }, [currentHighestBid, lotUserId]);

  return (
    <>
      <LoginPopup
        isOpen={displayLoginPopup}
        setIsOpen={setDisplayLoginPopup}
        onDismiss={() => {
          setIsSubmitting(false);
        }}
      >
        <LotLogInBanner
          lotId={parseInt(lotId)}
          auctionId={parseInt(auctionId as string)}
          message="Sign in to place a bid on this item."
          fallbackMessage={`Sign in to bid on "${lot.title}".`}
        />
      </LoginPopup>
      <BidPopup
        ref={savePaymentSelectionRef}
        isOpen={displayBidPopup}
        setIsOpen={setDisplayBidPopup}
        auctionId={parseInt(auctionId as string)}
        bidInfo={bidInfo}
        flash={bidInfo.flash}
        onDismiss={() => setIsSubmitting(false)}
        onConfirm={async () => {
          setIsSubmitting(true);
          await confirmBid(bidInfo);
          setIsSubmitting(false);
        }}
      />
      <Flex className={cn("lot-page-card", "max-sm:p-2 max-w-[500px]")} ref={focusPlaceholderRef}>
        <Flex direction="column" className="w-full m-2.5" gap="5">
          <Flex className="w-full" align="start">
            <CurrentBid
              auctionState={auctionState}
              startBid={lot.startBid!}
              currentBid={currentHighestBid?.amount || lot.startBid!}
              started={!!currentHighestBid?.userId}
              expired={expired}
              flash={state.flash}
              dispatch={dispatch}
            />
          </Flex>
          <BiddingCardWinnerMessage
            // Winner / outbid messaging is derived from the hydrated user id and currentHighestBid.
            visible={isCurrentUserHighestBidder()}
            expired={expired}
            hasUserBids={hasUserBids}
          />
          <Flex direction="column" className="my-2" gap="3" align="center">
            <Flex className={cn("w-[90%]", auctionType === AuctionType.HYBRID ? "w-full space-y-2 flex-col" : "")}>
              <OtherBids
                hasBids={!!currentHighestBid?.amount}
                currentBid={currentHighestBid?.amount || lot.startBid!}
                biddingSchedule={biddingSchedule}
                isCurrentUserWinner={isCurrentUserHighestBidder()}
                disabled={isSubmitting || disableBid}
                setIsDisabled={setIsSubmitting}
                expired={expired}
                onClick={(amount: number) => {
                  placeBid({ amount, bidType: auctionType === AuctionType.HYBRID ? BidType.STANDARD : BidType.MAX });
                }}
                auctionType={auctionType}
              />
              {auctionType === AuctionType.HYBRID && (
                <Separator className="my-4" />
              )}
            </Flex>
            <BiddingCardInput
              auctionState={auctionState}
              placeBid={placeBid}
              minBid={bidMin}
              error={error}
              isCurrentUserWinner={isCurrentUserHighestBidder()}
              expired={expired}
              isSubmitting={isSubmitting}
              disableBid={disableBid}
              disableMaxBid={auctionType === AuctionType.HYBRID}
              biddingSchedule={biddingSchedule}
            />
            <BidInstructions auctionType={auctionType} />
            <Separator className="my-5" />
            <LotBidHistory
              key={lotId}
              auctionState={auctionState}
              auctionId={auctionId as string}
              lotId={lotId.toString()}
            />
          </Flex>
        </Flex>
      </Flex>
    </>
  );
};

export default BiddingCard;
